import { makeStyles } from '@material-ui/core/styles';

import { CARD, KICKER, PILL_PRIMARY, PILL_SECONDARY, TEXT_LINK } from '../Home/HomeDashboard/dashboardStyles';
import { COLORS2, REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

// Below this the list columns fold under the name.
export const NARROW = '@media (max-width: 700px)';

const INPUT = {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: '50px',
    padding: '12px 16px',
    border: `1px solid ${REDESIGN_COLORS.line}`,
    borderRadius: '14px',
    background: REDESIGN_COLORS.cream,
    fontFamily: REDESIGN_FONTS.body,
    fontSize: '1rem',
    fontWeight: 600,
    color: REDESIGN_COLORS.dark,
    outline: 'none',
    '&::placeholder': {
        color: REDESIGN_COLORS.muted,
        fontWeight: 500,
        opacity: 1,
    },
    '&:focus': {
        borderColor: REDESIGN_COLORS.primary,
        boxShadow: '0 0 0 3px rgba(16, 169, 179, 0.15)',
    },
    '&:disabled': {
        color: REDESIGN_COLORS.muted,
    },
};

// Styles the group edit page's cards and lists share.
const useEditStyles = makeStyles(() => ({
    card: {
        ...CARD,
        padding: 'clamp(20px, 2.6vw, 30px)',
        marginBottom: '20px',
    },
    cardHead: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px 20px',
        marginBottom: '18px',
    },
    cardTitle: {
        margin: 0,
        fontSize: '1.2rem',
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    hint: {
        margin: '14px 0 0',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.86rem',
        fontWeight: 600,
    },
    field: {
        display: 'grid',
        gap: '6px',
        marginBottom: '16px',
    },
    label: {
        color: REDESIGN_COLORS.dark,
        fontSize: '0.86rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    input: INPUT,
    textarea: {
        ...INPUT,
        minHeight: '120px',
        lineHeight: 1.5,
        resize: 'vertical',
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px',
    },
    primary: {
        ...PILL_PRIMARY,
        minHeight: '48px',
    },
    secondary: {
        ...PILL_SECONDARY,
        minHeight: '48px',
    },
    link: TEXT_LINK,
    // Pill tabs, as in the session viewer.
    tabs: {
        display: 'inline-flex',
        padding: '4px',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '999px',
        background: REDESIGN_COLORS.cream,
    },
    tab: {
        minHeight: '36px',
        padding: '0 16px',
        border: 0,
        borderRadius: '999px',
        background: 'none',
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.88rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.body,
        cursor: 'pointer',
    },
    tabOn: {
        background: REDESIGN_COLORS.white,
        color: REDESIGN_COLORS.dark,
        boxShadow: '0 1px 3px rgba(6, 63, 69, 0.12)',
    },
    // People lists: a bordered table of rows inside the card.
    list: {
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '18px',
        overflow: 'hidden',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '40px minmax(0, 1.4fr) minmax(0, 1.6fr) auto',
        alignItems: 'center',
        gap: '14px',
        padding: '12px 18px',
        [NARROW]: {
            gridTemplateColumns: '40px minmax(0, 1fr) auto',
            gap: '12px',
            padding: '12px 14px',
        },
    },
    head: {
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        background: REDESIGN_COLORS.cream,
        [NARROW]: {
            display: 'none',
        },
    },
    headCell: KICKER,
    row: {
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        '&:last-child': {
            borderBottom: 0,
        },
    },
    avatar: {
        width: '40px',
        height: '40px',
        fontSize: '0.95rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        background: REDESIGN_COLORS.mist,
        color: REDESIGN_COLORS.link,
    },
    main: {
        minWidth: 0,
    },
    name: {
        margin: 0,
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        overflowWrap: 'anywhere',
    },
    // On phones the email folds under the name.
    sub: {
        display: 'none',
        margin: '2px 0 0',
        color: REDESIGN_COLORS.body,
        fontSize: '0.86rem',
        overflowWrap: 'anywhere',
        [NARROW]: {
            display: 'block',
        },
    },
    cell: {
        minWidth: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.92rem',
        overflowWrap: 'anywhere',
        [NARROW]: {
            display: 'none',
        },
    },
    rowActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
    },
    accept: {
        ...PILL_PRIMARY,
        minHeight: '40px',
        padding: '0 16px',
        fontSize: '0.88rem',
    },
    reject: {
        ...PILL_SECONDARY,
        minHeight: '40px',
        padding: '0 16px',
        fontSize: '0.88rem',
        borderColor: 'rgba(243, 83, 37, 0.4)',
        color: COLORS2.virtualRed,
        '&:hover': {
            borderColor: COLORS2.virtualRed,
            color: COLORS2.virtualRed,
        },
        '&:disabled': {
            cursor: 'default',
            opacity: 0.5,
        },
    },
    remove: {
        display: 'grid',
        placeItems: 'center',
        width: '36px',
        height: '36px',
        padding: 0,
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '50%',
        color: COLORS2.virtualRed,
        background: REDESIGN_COLORS.white,
        cursor: 'pointer',
        '&:hover': {
            borderColor: COLORS2.virtualRed,
        },
        '& svg': {
            width: '18px',
            height: '18px',
        },
    },
    empty: {
        padding: '18px 20px',
        border: `1px solid ${REDESIGN_COLORS.primaryBorderSoft}`,
        borderRadius: '16px',
        background: REDESIGN_COLORS.mist,
        color: REDESIGN_COLORS.body,
        fontSize: '0.95rem',
        lineHeight: 1.55,
    },
    emptyAction: {
        marginTop: '14px',
    },
}));

export default useEditStyles;
